import _has from 'lodash/has';
import _remove from 'lodash/remove';
import _toNumber from 'lodash/toNumber';
import _filter from 'lodash/filter';
import _conforms from 'lodash/conforms';

import React, { Component } from 'react';

import ContactOption from './ContactOption';

import i18n from '../../lib/reacti18n';
import Page from '../../lib/page';

import { trackLink } from '../../lib/analytics-helper';
import { contactDefault, contactTechnical } from '../../lib/ContactDefaultData';
import {
  getC3MarketChannelEligibility,
} from '../../lib/globalHelper';
import { contactChannels } from '../../common/commonConstants';

// Contact Panels message below the icon and label
const messages = {
  heading: i18n.message('smarthelp/contactLinksOption.message.heading'),
  community: i18n.message('smarthelp/contactLinksOption.message.community'),
  resolution: i18n.message('smarthelp/contactLinksOption.messageLabel.resolution.issues'),
  contact: i18n.message('smarthelp/contactLinksOption.message.contact'),
  business: i18n.message('smarthelp/contactLinksOption.message.business'),
  developer: i18n.message('smarthelp/contactLinksOption.message.developer'),
  braintree: i18n.message('smarthelp/contactLinksOption.message.braintree'),
  site: i18n.message('smarthelp/contactLinksOption.message.site'),
  feedback: i18n.message('smarthelp/contactLinksOption.message.feedback'),
};

// Contact Panels label
const labels = {
  community: i18n.message('smarthelp/contactLinksOption.label.community'),
  resolution: i18n.message('smarthelp/contactLinksOption.label.resolution'),
  contact: i18n.message('smarthelp/contactLinksOption.label.contact'),
  business: i18n.message('smarthelp/contactLinksOption.label.business'),
  developer: i18n.message('smarthelp/contactLinksOption.label.developer'),
  braintree: i18n.message('smarthelp/contactLinksOption.label.braintree'),
  site: i18n.message('smarthelp/contactLinksOption.label.site'),
  feedback: i18n.message('smarthelp/contactLinksOption.label.feedback'),
};

// importing function from page
const cobrand = Page.getCountry().toLowerCase();
// Checkout login Business user or content toggle switch to business url
const isBusinessUser = Page.getIsBusinessUser() || Page.getUserExperience() === 'business';

const supportMessageCSS = 'support-message';

const links = {
  community: Page.getCommunityLink(),
  resolution: Page.getResolutionURL(),
  contact: `/${cobrand}/smarthelp/contact-us`,
  business: `/${cobrand}/brc`,
  developer: 'https://developer.paypal.com/',
  braintree: 'https://articles.braintreepayments.com/',
  site: 'https://www.paypal-status.com/',
};

class ContactOptionsLinksFooter extends Component {
  // Validation condition: both 'messages' and 'labels' have the same key
  static keyValidation(channelName) {
    return _has(messages, channelName) && _has(labels, channelName);
  }

  static getLabel(channelName) {
    return labels[channelName];
  }

  static getMessage(channelName) {
    return messages[channelName];
  }

  static getLink(channelName) {
    return links[channelName];
  }

  static getChannelActionAndLink(channelName) {
    const redirectLink = ContactOptionsLinksFooter.getLink(channelName);
    if (redirectLink) {
      return {
        redirectLink,
        sameTab: channelName === 'contact',
        action: () => {
          trackLink({ id: Page.getFPTIPageId(), link: `smarthelp-enhance-footer|${channelName}` });
          if (channelName === 'contact') {
            window.location = redirectLink;
          } else {
            window.open(redirectLink, '_blank');
          }
        },
      };
    }
    return {};
  }

  static getIconClass(channelName) {
    return `vx_btn-link icon-${channelName}`;
  }

  /** Breaking rule class
   * If the number of channel less than 5, then using four padding rule
   * If the number of channel equal or more than 5, then using five padding rule
   */
  static getBreakLinkClass(channel) {
    const padNumber = (channel && channel.length <= 4) ? 'four' : 'five';
    return `contact-panels contact-panels-${padNumber}-padding vx_mainContent`;
  }

  // Display each of the Contact Option
  static getContactChannel(channel) {
    // key value is community, contact, resolution or business
    const key = channel.type;
    if (!ContactOptionsLinksFooter.keyValidation(key)) {
      return null;
    }

    const actionLink = ContactOptionsLinksFooter.getChannelActionAndLink(key);
    return (
      <li className="contact-option-list-item">
        <ContactOption
          key={key}
          id={key}
          contactLinkCSS="btn vx_btn-link contact-link"
          iconClass={ContactOptionsLinksFooter.getIconClass(key)}
          text={ContactOptionsLinksFooter.getLabel(key)}
          message={ContactOptionsLinksFooter.getMessage(key)}
          supportMessageCSS={supportMessageCSS}
          clickAction={actionLink.action}
          link={actionLink.redirectLink}
          sameTab={actionLink.sameTab}
        />
      </li>
    );
  }

  /**
   * Check whether the business or not
   * Business Resource available for en-US and en-CA only
   */
  static isBusinessChecker() {
    const BRCEnable = Page.isBrcEnabledMarket();

    if (!isBusinessUser || !BRCEnable) {
      _remove(contactDefault, (contact) => (contact.type === 'business'));
    }
  }

  // technical community link filter
  static techCommunityFilter() {
    // Check is English locale
    const regex = /^en/;
    const locale = regex.test(Page.getLocale());

    // In technical exp, displaying Community option for English locale
    return !Page.isTechnicalExp() || locale;
  }

  // Get change option with initial data
  static getContactOption() {
    if (Page.isTechnicalExp()) {
      return contactTechnical;
    }
    return contactDefault;
  }

  // Sort the display sequence by priority values
  static sortContactChannels() {
    return ContactOptionsLinksFooter.getContactOption().sort((channel1, channel2) => (
      _toNumber(channel1.priority) - _toNumber(channel2.priority)
    ));
  }

  // Filter for community link
  static filterContactChannels() {
    const contactOption = ContactOptionsLinksFooter.sortContactChannels();
    return _filter(contactOption,
      _conforms({ type: (type) => (links.community && ContactOptionsLinksFooter.techCommunityFilter()) || type !== 'community' }));
  }

  static renderContactPanels() {
    ContactOptionsLinksFooter.isBusinessChecker();
    const contactChannel = ContactOptionsLinksFooter.filterContactChannels();

    // if market is enabled in c3merchantMigration RC or if the user is c2merchantmigrated
    // and resolution is not available in channels remove resolution center
    if (!getC3MarketChannelEligibility(contactChannels.RESOLUTION)) {
      _remove(contactChannel, (contact) => (contact.type === contactChannels.RESOLUTION));
    }

    // if market is enabled in c3merchantMigration RC or if the user is c2merchantmigrated
    // and community is not available in channels remove community
    if (!getC3MarketChannelEligibility(contactChannels.COMMUNITY)) {
      _remove(contactChannel, (contact) => (contact.type === contactChannels.COMMUNITY));
    }

    return (
      <ul className={ContactOptionsLinksFooter.getBreakLinkClass(contactChannel)}>
        {contactChannel.map((channel) => ContactOptionsLinksFooter.getContactChannel(channel))}
      </ul>
    );
  }

  render() {
    return (
      <div className="contact-container-footer">
        <h2 className="contact-heading-label">{messages.heading}</h2>
        {ContactOptionsLinksFooter.renderContactPanels()}
      </div>
    );
  }
}

export default ContactOptionsLinksFooter;
